import { Fragment, useState, type ReactNode } from "react"
import type { SlotOccurrence } from "../types"
import {
  previewLabel,
  type PreviewAnnotation,
  type PreviewText,
} from "./presentation"

interface EntityContentProps {
  content: PreviewText
  onSelectOccurrence?: (occurrence: SlotOccurrence) => void
  allowLinkNavigation?: boolean
}

function Spoiler({ children }: { children: ReactNode }) {
  const [revealed, setRevealed] = useState(false)
  return (
    <span
      className={`telegram-preview__spoiler${revealed ? " telegram-preview__spoiler--revealed" : ""}`}
      role="button"
      tabIndex={0}
      aria-expanded={revealed}
      aria-label={revealed ? "Скрыть спойлер" : "Показать спойлер"}
      onClick={() => setRevealed(!revealed)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          setRevealed(!revealed)
        }
      }}
    >
      {children}
    </span>
  )
}

interface EntityNode {
  annotation: PreviewAnnotation
  children: EntityNode[]
}

/** Only called with validated codec ranges. Equal ranges retain distinct styles;
 * quotes are outer blocks, links remain inside styles, and source is never HTML. */
export function EntityContent({
  content,
  onSelectOccurrence,
  allowLinkNavigation,
}: EntityContentProps) {
  const rank = ({ entity }: PreviewAnnotation) =>
    ["blockquote", "expandable_blockquote"].includes(entity.type)
      ? 0
      : ["bold", "italic", "underline", "strikethrough", "spoiler"].includes(
            entity.type,
          )
        ? 1
        : 2
  const ordered = [...content.annotations].sort(
    (a, b) =>
      a.entity.offset - b.entity.offset ||
      b.entity.length - a.entity.length ||
      rank(a) - rank(b),
  )
  const roots: EntityNode[] = [],
    stack: EntityNode[] = []
  for (const annotation of ordered) {
    while (
      stack.length &&
      annotation.entity.offset >=
        stack[stack.length - 1].annotation.entity.offset +
          stack[stack.length - 1].annotation.entity.length
    )
      stack.pop()
    const node = { annotation, children: [] }
    if (stack.length) stack[stack.length - 1].children.push(node)
    else roots.push(node)
    stack.push(node)
  }
  const renderRange = (
    start: number,
    end: number,
    nodes: EntityNode[],
  ): ReactNode => {
    const result: ReactNode[] = []
    let cursor = start
    for (const node of nodes) {
      const { entity } = node.annotation
      result.push(content.text.slice(cursor, entity.offset))
      result.push(
        <Fragment key={`${entity.offset}:${entity.type}`}>
          {wrap(
            node.annotation,
            renderRange(
              entity.offset,
              entity.offset + entity.length,
              node.children,
            ),
          )}
        </Fragment>,
      )
      cursor = entity.offset + entity.length
    }
    result.push(content.text.slice(cursor, end))
    return result
  }
  const wrap = (
    annotation: PreviewAnnotation,
    children: ReactNode,
  ): ReactNode => {
    const { entity, href, occurrence, missing } = annotation
    switch (entity.type) {
      case "bold":
        return <strong>{children}</strong>
      case "italic":
        return <em>{children}</em>
      case "underline":
        return <u>{children}</u>
      case "strikethrough":
        return <s>{children}</s>
      case "code":
        return <code>{children}</code>
      case "pre":
        return (
          <pre
            title={entity.language ? previewLabel(entity.language) : undefined}
          >
            <code>{children}</code>
          </pre>
        )
      case "spoiler":
        return <Spoiler>{children}</Spoiler>
      case "blockquote":
        return <blockquote>{children}</blockquote>
      case "expandable_blockquote":
        return (
          <details>
            <summary>Цитата</summary>
            <blockquote>{children}</blockquote>
          </details>
        )
      case "date_time": {
        const date = new Date(entity.unix_time * 1000)
        return (
          <time
            dateTime={
              Number.isFinite(date.getTime()) ? date.toISOString() : undefined
            }
            title={entity.date_time_format}
          >
            {children}
          </time>
        )
      }
      case "text_mention": {
        const user = entity.user
        return (
          <span
            className="telegram-preview__mention"
            title={previewLabel(
              [
                [user.first_name, user.last_name].filter(Boolean).join(" "),
                user.username ? `@${user.username}` : "",
                `ID ${user.id}`,
              ]
                .filter(Boolean)
                .join(" · "),
            )}
          >
            {children}
          </span>
        )
      }
      case "mention_by_id":
        return (
          <span
            className="telegram-preview__mention"
            title={`Telegram ID ${entity.userId}`}
          >
            {children}
          </span>
        )
      case "text_link":
      case "url":
        if (annotation.passive)
          return (
            <span
              className={missing ? "telegram-preview__missing" : undefined}
              title={missing ? "Не задано значение ссылки" : undefined}
            >
              {children}
            </span>
          )
        return (
          <>
            <a
              className={missing ? "telegram-preview__missing" : undefined}
              href={href}
              role={href ? undefined : "link"}
              tabIndex={0}
              aria-disabled={!href && !(occurrence && onSelectOccurrence)}
              title={missing ? "Не задано значение ссылки" : undefined}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => {
                if (
                  !allowLinkNavigation ||
                  !href ||
                  (occurrence && onSelectOccurrence)
                )
                  event.preventDefault()
                if (occurrence && onSelectOccurrence) {
                  event.stopPropagation()
                  onSelectOccurrence(occurrence)
                }
              }}
              onAuxClick={(event) => {
                if (
                  !allowLinkNavigation ||
                  !href ||
                  (occurrence && onSelectOccurrence)
                ) {
                  event.preventDefault()
                  event.stopPropagation()
                }
              }}
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" &&
                  !href &&
                  occurrence &&
                  onSelectOccurrence
                ) {
                  event.preventDefault()
                  event.stopPropagation()
                  onSelectOccurrence(occurrence)
                }
              }}
            >
              {children}
            </a>
            {onSelectOccurrence &&
              annotation.textSlots?.map((slot, index) => (
                <button
                  type="button"
                  key={index}
                  className="telegram-preview__context"
                  aria-label={`Выбрать ${slot.label}: текст ссылки`}
                  onClick={(event) => {
                    event.stopPropagation()
                    onSelectOccurrence(slot.occurrence)
                  }}
                >
                  Текст: {slot.label}
                </button>
              ))}
          </>
        )
      case "mention":
      case "hashtag":
      case "cashtag":
      case "bot_command":
      case "email":
      case "phone_number":
        return <span className="telegram-preview__mention">{children}</span>
      case "custom_emoji":
        return (
          <span title="Пользовательский эмодзи (исходный символ)">
            {children}
          </span>
        )
      default:
        return <span title="Неподдерживаемая аннотация">{children}</span>
    }
  }
  return <>{renderRange(0, content.text.length, roots)}</>
}
